// Which palette the app wears. Factory-owned.
//
// FOLLOWS THE PHONE by default. Design and review light first, then verify dark
// is equally good, but never force light. Ionic's `palettes/dark.system.css`
// keys on the OS preference and nothing else; main.js imports `dark.class.css`
// instead, which does nothing until `.ion-palette-dark` is on <html>, and this
// module is the only thing that puts it there:
//
//   'system' (default)  while the OS asks for dark, following changes live
//   'light'             never (pinned)
//   'dark'              always (pinned)
//
// The value is `brand.colorScheme` in app.config.json, written into
// app-config.js by sync. Nothing here reads the config file, which the browser
// cannot.

export const SCHEMES = ['light', 'system', 'dark'];

/** Anything unrecognised follows the phone. */
export function normalizeScheme(value) {
  return SCHEMES.includes(value) ? value : 'system';
}

/** Does this scheme, on a device that does or does not prefer dark, wear dark? */
export function wearsDark(scheme, osPrefersDark) {
  const s = normalizeScheme(scheme);
  return s === 'dark' || (s === 'system' && !!osPrefersDark);
}

/**
 * Put the answer on <html>. Takes its inputs as arguments so a test can hand it
 * a stand-in document instead of a browser.
 *
 * `color-scheme` is set alongside the class because the class only moves Ionic's
 * variables: the BROWSER still decides scrollbars, form controls and the canvas
 * behind an overscroll, and a light app on a dark phone must say it is light.
 */
export function applyScheme(scheme, osPrefersDark, root = document.documentElement) {
  const dark = wearsDark(scheme, osPrefersDark);
  root.classList.toggle('ion-palette-dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
  root.dataset.scheme = dark ? 'dark' : 'light';
  return dark;
}

const LIGHT_CHROME = '#ffffff';
const DARK_CHROME = '#121212';

/**
 * Make the <meta> tags agree with the scheme.
 *
 * index.html is rendered once, when the app is scaffolded, and `sync` only
 * reconciles its SEO block — so an app that flips `brand.colorScheme` later
 * would keep the metas it was born with (a light-only address bar on a dark app,
 * or the reverse). The script is what runs on every build, so it owns the answer:
 * one theme-color for a light-only or dark-only app, the media-keyed pair only
 * for 'system', where the app can be either.
 */
export function applyMeta(scheme, doc = document) {
  const s = normalizeScheme(scheme);
  const head = doc.head;
  head.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.remove());
  const add = (content, media) => {
    const m = doc.createElement('meta');
    m.name = 'theme-color';
    m.content = content;
    if (media) m.media = media;
    head.appendChild(m);
  };
  if (s === 'system') {
    add(LIGHT_CHROME, '(prefers-color-scheme: light)');
    add(DARK_CHROME, '(prefers-color-scheme: dark)');
  } else {
    add(s === 'dark' ? DARK_CHROME : LIGHT_CHROME);
  }
  let cs = head.querySelector('meta[name="color-scheme"]');
  if (!cs) { cs = doc.createElement('meta'); cs.name = 'color-scheme'; head.appendChild(cs); }
  cs.content = s === 'system' ? 'light dark' : s;
}

/**
 * Apply now and, for 'system', keep following the OS. Returns a stop function.
 * A light-only or dark-only app registers no listener at all.
 */
export function initScheme(scheme, { win = window, root = win.document.documentElement } = {}) {
  const mq = typeof win.matchMedia === 'function' ? win.matchMedia('(prefers-color-scheme: dark)') : null;
  applyScheme(scheme, mq?.matches, root);
  if (win.document?.head) applyMeta(scheme, win.document);
  if (!mq || normalizeScheme(scheme) !== 'system') return () => {};
  const onChange = (e) => applyScheme(scheme, e.matches, root);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
